import { buildTutorialTargetMissingHint } from '../buildTargetMissingHint';
import type { TutorialDefinition, TutorialRecover } from '../types';

const R = {
  page: {
    path: '/workspace-evaluation-plan',
    pageLabel: '기본 평가계획 등록',
    openSelectors: [
      '#cp-sidebar-class-common-trigger',
      'a[href^="/workspace-evaluation-plan"]',
    ],
    openLabel: '기본 평가계획 등록',
    accessLabel: '사이드바 「학습내용, 평가계획」',
  },
  levelModal: {
    path: '/workspace-evaluation-plan',
    openSelector: '[data-tutorial-ep="level-settings"]',
    pageLabel: '기본 평가계획 등록',
    openLabel: '성취수준 표시 설정',
  },
  categoryModal: {
    path: '/workspace-evaluation-plan',
    openSelector: '[data-tutorial-ep="add-category"], [aria-label="사용자 카테고리 추가"]',
    pageLabel: '기본 평가계획 등록',
    openLabel: '사용자 카테고리 추가',
  },
  standardModal: {
    path: '/workspace-evaluation-plan',
    openSelector: '[data-tutorial-ep="add-standard"], [aria-label="성취기준 추가"]',
    pageLabel: '기본 평가계획 등록',
    openLabel: '성취기준 추가',
  },
  planModal: {
    path: '/workspace-evaluation-plan',
    openSelector: '[data-tutorial-ep="add-plan"], [aria-label="평가계획 추가"]',
    pageLabel: '기본 평가계획 등록',
    openLabel: '평가계획 추가',
  },
} as const satisfies Record<string, TutorialRecover>;

const RECOVER_BY_STEP_ID: Record<string, TutorialRecover> = {
  'go-evaluation-plan': R.page,
  overview: R.page,
  'legend-guide': R.page,
  'category-tabs-guide': R.page,
  'open-add-category': R.page,
  'add-category-modal': R.categoryModal,
  'open-level-settings': R.page,
  'level-settings-modal': R.levelModal,
  'standards-guide': R.page,
  'open-add-standard': R.page,
  'standard-form-practice': R.standardModal,
  'plans-guide': R.page,
  'open-add-plan': R.page,
  'plan-form-level-step': R.planModal,
  'plan-open-criterion': R.planModal,
  'plan-attach-choose-criterion': R.planModal,
  'plan-web-doc-templates': R.planModal,
  'plan-web-doc-table': R.planModal,
  'plan-web-doc-circle-add': R.planModal,
  'plan-web-doc-save-template': R.planModal,
  'plan-web-doc-apply-plan': R.planModal,
  'plan-web-doc-memo': R.planModal,
  'plan-web-doc-review': R.planModal,
  'plan-open-sheet': R.planModal,
  'plan-attach-choose-sheet': R.planModal,
  'plan-sheet-overview': R.planModal,
  'plan-sheet-tool-text': R.planModal,
  'plan-sheet-tool-shape': R.planModal,
  'plan-sheet-tool-link-image': R.planModal,
  'plan-sheet-tool-clipboard-image': R.planModal,
  'plan-sheet-tool-scan-image': R.planModal,
  'plan-sheet-tool-table': R.planModal,
  'plan-sheet-tool-undo': R.planModal,
  'plan-sheet-tool-redo': R.planModal,
  'plan-sheet-tool-snap': R.planModal,
  'plan-sheet-tool-front': R.planModal,
  'plan-sheet-tool-back': R.planModal,
  'plan-sheet-tool-align': R.planModal,
  'plan-sheet-review': R.planModal,
  'plan-form-practice': R.planModal,
  'apply-guide': R.page,
  'applied-guide': R.page,
  'save-all': R.page,
};

const evaluationPlanRaw: TutorialDefinition = {
  id: 'evaluation-plan',
  title: '기본 평가계획 등록',
  summary:
    '사용자 카테고리·성취수준 표시 설정과 성취기준·평가계획·클래스 평가계획 등록 흐름을 배워요.',
  steps: [
    {
      id: 'intro',
      mascot:
        '기본 평가계획 등록 튜토리얼을 시작해요! 카테고리와 성취수준 표시를 이해한 뒤, 성취기준·평가계획을 등록하고 클래스에 적용해 볼게요.',
      actionLabel: '시작하기',
      presentation: 'intro',
      cardTitle: '기본 평가계획 등록을 시작해요!',
      placement: 'center',
    },
    {
      id: 'go-evaluation-plan',
      mascot:
        '사이드바에서 「학습내용, 평가계획」을 펼친 뒤 "기본 평가계획 등록"을 눌러 이동해 주세요.',
      targetSelector:
        '#cp-sidebar-class-common-trigger, a[href^="/workspace-evaluation-plan"]',
      placement: 'right',
      advanceOnPathPrefix: '/workspace-evaluation-plan',
      section: '[평가계획] 페이지 열기',
      sectionMascot: '기본 평가계획 등록 화면으로 이동하는 구간이에요.',
    },
    {
      id: 'overview',
      mascot:
        '이 화면에서는 성취기준과 평가계획을 만들고, 선택한 클래스의 평가계획으로 적용할 수 있어요. 전체 구성을 살펴본 뒤 "다음"을 눌러 주세요.',
      targetSelector: '[data-tutorial-ep="page"]',
      manualAdvance: true,
      blockTargetClick: true,
      section: '[평가계획] 화면·카테고리',
      sectionMascot: '범주와 카테고리를 알아보는 구간이에요.',
    },
    {
      id: 'legend-guide',
      mascot:
        '점 색이 카테고리 출처예요. [[dot:default]] 관리자 기본(읽기 전용), [[dot:team]] 팀장(읽기 전용), [[dot:user]] 내 사용자 카테고리(편집·저장 가능)입니다. 확인해 본 뒤 "다음"을 눌러 주세요.',
      targetSelector: '[data-tutorial-ep="legend"]',
      manualAdvance: true,
      blockTargetClick: true,
    },
    {
      id: 'category-tabs-guide',
      mascot:
        '카테고리 탭을 눌러 기본·팀장·사용자 자료를 전환할 수 있어요. 같은 이름이라도 출처가 다르면 데이터가 분리됩니다. 탭을 눌러 본 뒤 "다음"을 눌러 주세요.',
      targetSelector: '[data-tutorial-ep="category-tabs"], [aria-label="카테고리"]',
      manualAdvance: true,
    },
    {
      id: 'open-add-category',
      mascot: '"사용자 카테고리 추가하기 +"를 눌러 새 카테고리 창을 열어 주세요.',
      targetSelector: '[data-tutorial-ep="add-category"], [aria-label="사용자 카테고리 추가"]',
      advanceWhenSelectorExists: '[data-tutorial-ep="add-category-modal"]',
      section: '[평가계획] 사용자 카테고리',
      sectionMascot: '내 카테고리를 만드는 구간이에요.',
    },
    {
      id: 'add-category-modal',
      mascot:
        '카테고리 이름과 성취수준 단계를 정한 뒤 "추가"를 누르면 만들어지고 다음으로 이어져요. "취소"를 눌러도 건너뛰고 다음으로 가요.',
      targetSelector: '[data-tutorial-ep="add-category-modal"]',
      // 취소·추가(또는 모달 닫힘)면 다음 단계로 진행
      advanceSelector:
        '[data-tutorial-ep="add-category-modal"] .cp-btn-secondary, [data-tutorial-ep="add-category-modal"] .cp-btn-primary',
      advanceWhenSelectorGone: '[data-tutorial-ep="add-category-modal"]',
      manualAdvance: true,
      allowManualAdvanceActions: true,
    },
    {
      id: 'open-level-settings',
      mascot: '"성취수준 표시 설정" 버튼을 눌러 설정 창을 열어 주세요.',
      targetSelector: '[data-tutorial-ep="level-settings"]',
      advanceWhenSelectorExists:
        '[data-tutorial-ep="level-settings-modal"], .cp-evaluation-plan-level-settings-modal',
      section: '[평가계획] 성취수준 표시 설정',
      sectionMascot: '단계별 표시 라벨을 정하는 구간이에요.',
    },
    {
      id: 'level-settings-modal',
      mascot:
        '기본 성취수준 단계와 단계별 표시 라벨을 정할 수 있어요. 새 카테고리·평가계획에 기본값으로 쓰입니다. "저장" 또는 "취소"를 누르면 다음으로 이어져요.',
      targetSelector:
        '[data-tutorial-ep="level-settings-modal"], .cp-evaluation-plan-level-settings-modal',
      // 취소·저장(또는 모달 닫힘)면 다음 단계로 진행
      advanceSelector:
        '[data-tutorial-ep="level-settings-modal"] .cp-btn-secondary, [data-tutorial-ep="level-settings-modal"] .cp-btn-primary, .cp-evaluation-plan-level-settings-modal .cp-btn-secondary, .cp-evaluation-plan-level-settings-modal .cp-btn-primary',
      advanceWhenSelectorGone:
        '[data-tutorial-ep="level-settings-modal"], .cp-evaluation-plan-level-settings-modal',
      manualAdvance: true,
      allowManualAdvanceActions: true,
    },
    {
      id: 'standards-guide',
      mascot:
        '왼쪽 「성취기준 목록」에는 평가의 기준이 되는 성취기준이 모여 있어요. 검색·일괄등록도 가능합니다. 살펴본 뒤 "다음"을 눌러 주세요.',
      targetSelector: '[data-tutorial-ep="standards-block"], .cp-evaluation-plan-standards-block',
      manualAdvance: true,
      blockTargetClick: true,
      section: '[평가계획] 성취기준 목록',
      sectionMascot: '성취기준을 등록해 보는 구간이에요.',
      targetMissingHint:
        '카테고리를 선택해야 목록이 나타나요. 사용자 카테고리를 고른 뒤 다시 보거나 "다음"으로 넘어가도 돼요.',
    },
    {
      id: 'open-add-standard',
      mascot: '성취기준 목록의 "+"를 눌러 등록 창을 열어 주세요.',
      targetSelector: '[data-tutorial-ep="add-standard"], [aria-label="성취기준 추가"]',
      advanceWhenSelectorExists:
        '[data-tutorial-ep="standard-form-modal"], .cp-evaluation-plan-standard-form-modal',
    },
    {
      id: 'standard-form-practice',
      mascot:
        '성취기준 코드·내용 등을 채워 등록해 보세요. "등록"을 누르면 바로 저장되고 다음으로 이어져요. "취소"를 눌러도 다음으로 넘어갑니다.',
      targetSelector:
        '[data-tutorial-ep="standard-form-modal"], .cp-evaluation-plan-standard-form-modal',
      // 취소·등록(또는 모달 닫힘)면 다음 단계로 진행
      advanceSelector:
        '[data-tutorial-ep="standard-form-modal"] .cp-btn-secondary, [data-tutorial-ep="standard-form-modal"] .cp-btn-primary, .cp-evaluation-plan-standard-form-modal .cp-btn-secondary, .cp-evaluation-plan-standard-form-modal .cp-btn-primary',
      advanceWhenSelectorGone:
        '[data-tutorial-ep="standard-form-modal"], .cp-evaluation-plan-standard-form-modal',
      manualAdvance: true,
      allowManualAdvanceActions: true,
    },
    {
      id: 'plans-guide',
      mascot:
        '가운데 「평가계획 목록」은 성취기준과 연결된 평가 계획을 관리해요. 체크한 항목을 클래스에 적용할 수 있습니다. 살펴본 뒤 "다음"을 눌러 주세요.',
      targetSelector: '[data-tutorial-ep="plans-block"], .cp-evaluation-plan-plans-block',
      manualAdvance: true,
      blockTargetClick: true,
      section: '[평가계획] 평가계획 목록',
      sectionMascot: '평가계획을 등록해 보는 구간이에요.',
    },
    {
      id: 'open-add-plan',
      mascot: '평가계획 목록의 "+"를 눌러 등록 창을 열어 주세요.',
      targetSelector: '[data-tutorial-ep="add-plan"], [aria-label="평가계획 추가"]',
      advanceWhenSelectorExists:
        '[data-tutorial-ep="plan-form-modal"], .cp-evaluation-plan-plan-form-modal',
    },
    {
      id: 'plan-form-level-step',
      mascot:
        '오른쪽 위 "성취수준 단계"에서 이 평가계획의 A·B·C… 단계 수를 정해요. 바꾸면 아래 성취수준 입력칸 개수도 맞춰집니다. 확인해 본 뒤 "다음"을 눌러 주세요.',
      targetSelector:
        '[data-tutorial-ep="plan-level-step"], [aria-label="성취수준 단계"]',
      manualAdvance: true,
      section: '[평가계획] 등록 모달 · 기준안·평가지',
      sectionMascot: '평가계획 등록 창에서 단계·기준안·평가지를 알아보는 구간이에요.',
    },
    {
      id: 'plan-open-criterion',
      mascot:
        '"기준안 등록"을 눌러 등록 방식 선택 창을 열어 주세요. 이미 등록돼 있다면 "수정" 아이콘을 눌러 주세요.',
      targetSelector:
        '[data-tutorial-ep="plan-register-criterion"], [data-tutorial-ep="plan-register-criterion"] [aria-label="기준안 등록됨 수정"]',
      advanceWhenSelectorExists:
        '[data-tutorial-ep="attach-register-modal"], [data-tutorial-ep="web-doc-modal"]',
    },
    {
      id: 'plan-attach-choose-criterion',
      mascot:
        '기준안은 두 가지로 등록할 수 있어요.\n· 웹 문서 생성: 앱에서 바로 표를 작성합니다.\n· 공유링크 업로드: Google Drive 등 공유 URL을 등록합니다.\n지금은 "웹 문서 생성"을 눌러 작성 화면으로 들어가 주세요.',
      targetSelector: '[data-tutorial-ep="attach-options"], [aria-label="등록 방식"]',
      advanceSelector: '[data-tutorial-ep="attach-option-web-doc"]',
      advanceImmediately: true,
      advanceWhenSelectorExists: '[data-tutorial-ep="web-doc-modal"]',
      manualAdvance: true,
      blockClickSelectors: '[data-tutorial-ep="attach-option-share-link"]',
    },
    {
      id: 'plan-web-doc-templates',
      mascot:
        '위쪽 "기준안 양식"에서 저장해 둔 표 틀(양식)을 고를 수 있어요. 양식마다 행·열 구조가 다를 수 있습니다. 확인해 본 뒤 "다음"을 눌러 주세요.',
      targetSelector:
        '[data-tutorial-ep="web-doc-templates"], [aria-label="기준안 양식"]',
      manualAdvance: true,
      allowManualAdvanceActions: true,
      blockTargetClick: true,
    },
    {
      id: 'plan-web-doc-table',
      mascot:
        '이 표에 기준안 내용을 입력해요. 영역·성취기준·평가 수준 등을 칸에 채워 구성합니다. 확인해 본 뒤 "다음"을 눌러 주세요.',
      targetSelector: '[data-tutorial-ep="web-doc-table"]',
      manualAdvance: true,
      allowManualAdvanceActions: true,
      blockTargetClick: true,
    },
    {
      id: 'plan-web-doc-circle-add',
      mascot:
        '원형 "+" 버튼으로 표의 행이나 열을 추가할 수 있어요. "+"를 눌러 보거나, 눈으로만 확인한 뒤 "다음"을 눌러 주세요.',
      targetSelector:
        '[data-tutorial-ep="web-doc-circle-add"], .cp-evaluation-plan-web-doc-circle-add-btn',
      manualAdvance: true,
      allowManualAdvanceActions: true,
      spotlightSeparate: true,
      targetMissingHint:
        '표 구조에 따라 "+"가 안 보일 수 있어요. "다음"으로 넘어가도 돼요.',
    },
    {
      id: 'plan-web-doc-save-template',
      mascot:
        '행·열 같은 틀을 바꾼 뒤에는 "기준안 양식 저장"으로 그 구조를 양식으로 남겨 둘 수 있어요. 확인해 본 뒤 "다음"을 눌러 주세요.',
      targetSelector: '[data-tutorial-ep="web-doc-save-template"]',
      manualAdvance: true,
      allowManualAdvanceActions: true,
      blockTargetClick: true,
    },
    {
      id: 'plan-web-doc-apply-plan',
      mascot:
        '"평가계획 데이터 반영"은 평가계획에 적어 둔 영역·성취기준 등을 표에 자동으로 채워 주는 버튼이에요. 확인해 본 뒤 "다음"을 눌러 주세요.',
      targetSelector: '[data-tutorial-ep="web-doc-apply-plan"]',
      manualAdvance: true,
      allowManualAdvanceActions: true,
      blockTargetClick: true,
    },
    {
      id: 'plan-web-doc-memo',
      mascot:
        '구분 열의 "메모" 칸에는 평가수준 기준 점수처럼 참고할 정보를 적어 둘 수 있어요. 확인해 본 뒤 "다음"을 눌러 주세요.',
      targetSelector: '[data-tutorial-ep="web-doc-gubun-memo-cell"]',
      manualAdvance: true,
      allowManualAdvanceActions: true,
      blockTargetClick: true,
      spotlightUnion: true,
      targetMissingHint:
        '구분 열 메모 칸이 보이지 않으면 표를 스크롤해 보거나 "다음"으로 넘어가도 돼요.',
    },
    {
      id: 'plan-web-doc-review',
      mascot:
        '기준안 웹 문서를 자유롭게 구성해 보세요. 닫기·등록은 잠겨 있어요. 충분히 살펴본 뒤 "다음"을 눌러 주세요(모달이 닫힙니다).',
      targetSelector: '[data-tutorial-ep="web-doc-modal"]',
      advanceWhenSelectorGone: '[data-tutorial-ep="web-doc-modal"]',
      manualAdvance: true,
      allowManualAdvanceActions: true,
      blockClickSelectors:
        '[data-tutorial-ep="web-doc-cancel"], [data-tutorial-ep="web-doc-save"]',
    },
    {
      id: 'plan-open-sheet',
      mascot:
        '이어서 "평가지 등록"을 눌러 등록 방식 선택 창을 열어 주세요. 이미 등록돼 있다면 "수정" 아이콘을 눌러 주세요.',
      targetSelector:
        '[data-tutorial-ep="plan-register-sheet"], [data-tutorial-ep="plan-register-sheet"] [aria-label="평가지 등록됨 수정"]',
      advanceWhenSelectorExists:
        '[data-tutorial-ep="attach-register-modal"], [data-tutorial-ep="sheet-canvas-modal"]',
      targetMissingHint:
        '평가계획 등록 창이 열려 있어야 해요. "+"로 등록 창을 연 뒤 다시 진행해 주세요.',
    },
    {
      id: 'plan-attach-choose-sheet',
      mascot:
        '평가지도 기준안과 같이 웹 문서 생성·공유링크 업로드 중 하나로 등록해요.\n· 웹 문서 생성: A4 캔버스에 개체 블럭으로 평가지를 구성합니다.\n· 공유링크 업로드: 외부 공유 URL을 등록합니다.\n지금은 "웹 문서 생성"을 눌러 캔버스로 들어가 주세요.',
      targetSelector: '[data-tutorial-ep="attach-options"], [aria-label="등록 방식"]',
      advanceSelector: '[data-tutorial-ep="attach-option-web-doc"]',
      advanceImmediately: true,
      advanceWhenSelectorExists:
        '[data-tutorial-ep="sheet-canvas-modal"], .cp-evaluation-plan-sheet-canvas-modal:not(.cp-evaluation-plan-sheet-canvas-viewer-modal)',
      manualAdvance: true,
      blockClickSelectors: '[data-tutorial-ep="attach-option-share-link"]',
    },
    {
      id: 'plan-sheet-overview',
      mascot:
        '평가지 A4 캔버스예요. 이제 왼쪽·오른쪽 도구를 하나씩 살펴본 뒤, 마지막에 자유롭게 작성해 볼게요. "다음"을 눌러 주세요.',
      targetSelector: '[data-tutorial-ep="sheet-canvas-modal"]',
      manualAdvance: true,
      blockTargetClick: true,
    },
    {
      id: 'plan-sheet-tool-text',
      mascot:
        '왼쪽 "텍스트 블럭"은 글상자를 캔버스에 추가해요. 제목·안내 문구 등에 씁니다. "다음"을 눌러 주세요.',
      targetSelector: '[data-tutorial-ep="sheet-toolbar-left"] [aria-label="텍스트 블럭 추가"]',
      manualAdvance: true,
      blockTargetClick: true,
      targetMissingHint: '평가지 캔버스 왼쪽 도구가 보이면 다시 확인해 주세요. "다음"으로 넘어가도 돼요.',
    },
    {
      id: 'plan-sheet-tool-shape',
      mascot:
        '"도형 블럭" 메뉴에서는 직사각형·원형·직선 도형을 넣을 수 있어요. 구분선·박스 강조에 활용합니다. "다음"을 눌러 주세요.',
      targetSelector: '[data-tutorial-ep="sheet-toolbar-left"] [aria-label="도형 블럭 추가 메뉴"]',
      manualAdvance: true,
      blockTargetClick: true,
    },
    {
      id: 'plan-sheet-tool-link-image',
      mascot: '"링크 이미지"는 URL로 이미지를 캔버스에 넣어요. "다음"을 눌러 주세요.',
      targetSelector: '[data-tutorial-ep="sheet-toolbar-left"] [aria-label="링크 이미지 블럭 추가"]',
      manualAdvance: true,
      blockTargetClick: true,
    },
    {
      id: 'plan-sheet-tool-clipboard-image',
      mascot:
        '"클립보드 이미지"는 복사한 그림을 붙여넣어 넣어요(Ctrl+V). "다음"을 눌러 주세요.',
      targetSelector:
        '[data-tutorial-ep="sheet-toolbar-left"] [aria-label="클립보드 이미지 블럭 추가 (Ctrl+V로 붙여넣기)"]',
      manualAdvance: true,
      blockTargetClick: true,
    },
    {
      id: 'plan-sheet-tool-scan-image',
      mascot: '"스캔 이미지"는 웹캠·스캔으로 가져온 이미지를 넣어요. "다음"을 눌러 주세요.',
      targetSelector: '[data-tutorial-ep="sheet-toolbar-left"] [aria-label="스캔 이미지 블럭 추가"]',
      manualAdvance: true,
      blockTargetClick: true,
    },
    {
      id: 'plan-sheet-tool-table',
      mascot:
        '"표 블럭"은 행·열 표를 캔버스에 추가해요. 채점표·문항표 구성에 자주 씁니다. "다음"을 눌러 주세요.',
      targetSelector: '[data-tutorial-ep="sheet-toolbar-left"] [aria-label="표 블럭 추가"]',
      manualAdvance: true,
      blockTargetClick: true,
    },
    {
      id: 'plan-sheet-tool-undo',
      mascot: '오른쪽 "되돌리기"는 바로 전 편집을 취소해요. "다음"을 눌러 주세요.',
      targetSelector: '[data-tutorial-ep="sheet-toolbar-right"] [aria-label="편집 되돌리기"]',
      manualAdvance: true,
      blockTargetClick: true,
    },
    {
      id: 'plan-sheet-tool-redo',
      mascot: '"다시 실행"은 되돌리기를 다시 적용해요. "다음"을 눌러 주세요.',
      targetSelector: '[data-tutorial-ep="sheet-toolbar-right"] [aria-label="편집 다시 실행"]',
      manualAdvance: true,
      blockTargetClick: true,
    },
    {
      id: 'plan-sheet-tool-snap',
      mascot:
        '"스마트 스냅"은 개체를 움직일 때 정렬선을 도와주는 기능이에요. 켜고 끌 수 있습니다. "다음"을 눌러 주세요.',
      targetSelector:
        '[data-tutorial-ep="sheet-toolbar-right"] [aria-label="스마트 스냅 끄기"], [data-tutorial-ep="sheet-toolbar-right"] [aria-label="스마트 스냅 켜기"]',
      manualAdvance: true,
      blockTargetClick: true,
    },
    {
      id: 'plan-sheet-tool-front',
      mascot: '"맨앞으로 보내기"는 선택한 개체를 가장 위로 올려요. "다음"을 눌러 주세요.',
      targetSelector: '[data-tutorial-ep="sheet-toolbar-right"] [aria-label="개체 맨앞으로 보내기"]',
      manualAdvance: true,
      blockTargetClick: true,
    },
    {
      id: 'plan-sheet-tool-back',
      mascot: '"맨뒤로 보내기"는 선택한 개체를 가장 아래로 내려요. "다음"을 눌러 주세요.',
      targetSelector: '[data-tutorial-ep="sheet-toolbar-right"] [aria-label="개체 맨뒤로 보내기"]',
      manualAdvance: true,
      blockTargetClick: true,
    },
    {
      id: 'plan-sheet-tool-align',
      mascot:
        '정렬 도구로 왼쪽·중앙·오른쪽·위·아래 정렬과 가로·세로 등간격 정렬을 할 수 있어요. 확인해 본 뒤 "다음"을 눌러 주세요.',
      targetSelector:
        '[data-tutorial-ep="sheet-toolbar-right"] [aria-label="왼쪽정렬"], [data-tutorial-ep="sheet-toolbar-right"] [aria-label="중앙정렬"], [data-tutorial-ep="sheet-toolbar-right"] [aria-label="오른쪽정렬"], [data-tutorial-ep="sheet-toolbar-right"] [aria-label="위쪽정렬"], [data-tutorial-ep="sheet-toolbar-right"] [aria-label="상하중앙정렬"], [data-tutorial-ep="sheet-toolbar-right"] [aria-label="아래정렬"], [data-tutorial-ep="sheet-toolbar-right"] [aria-label="가로 등간격 정렬"], [data-tutorial-ep="sheet-toolbar-right"] [aria-label="세로 등간격 정렬"]',
      manualAdvance: true,
      blockTargetClick: true,
      spotlightSeparate: true,
    },
    {
      id: 'plan-sheet-review',
      mascot:
        '평가지를 자유롭게 구성해 보세요. 닫기·등록은 잠겨 있어요. 충분히 살펴본 뒤 "다음"을 눌러 주세요(모달이 닫힙니다).',
      targetSelector: '[data-tutorial-ep="sheet-canvas-modal"]',
      advanceWhenSelectorGone: '[data-tutorial-ep="sheet-canvas-modal"]',
      manualAdvance: true,
      allowManualAdvanceActions: true,
      blockClickSelectors: '[data-tutorial-ep="sheet-cancel"], [data-tutorial-ep="sheet-save"]',
    },
    {
      id: 'plan-form-practice',
      mascot:
        '다시 평가계획 등록 창이에요. 영역·성취기준·평가방법·성취수준 등을 채운 뒤 "저장"을 누르면 바로 저장되고 목록에 추가돼요. 이미 등록했다면 창을 닫거나 "다음"을 눌러 주세요(모달이 닫힙니다).',
      targetSelector:
        '[data-tutorial-ep="plan-form-modal"], .cp-evaluation-plan-plan-form-modal',
      advanceWhenSelectorGone:
        '[data-tutorial-ep="plan-form-modal"], .cp-evaluation-plan-plan-form-modal',
      manualAdvance: true,
      allowManualAdvanceActions: true,
    },
    {
      id: 'apply-guide',
      mascot:
        '평가계획 목록에서 항목을 체크한 뒤 가운데 "→"(적용) 버튼을 누르면 오른쪽 「클래스 평가계획 등록」으로 들어가요. 가능하면 한 번 적용해 본 뒤 "다음"을 눌러 주세요.',
      targetSelector:
        '[data-tutorial-ep="plans-block"], .cp-evaluation-plan-plans-block, [data-tutorial-ep="apply-arrow"], .cp-evaluation-plan-apply-arrow',
      manualAdvance: true,
      spotlightSeparate: true,
      // 적용 연습에 집중: 새 평가계획 추가(+)는 잠근다.
      blockClickSelectors: '[data-tutorial-ep="add-plan"], [aria-label="평가계획 추가"]',
      section: '[평가계획] 클래스 평가계획 등록',
      sectionMascot: '선택한 평가계획을 클래스에 적용하는 구간이에요.',
    },
    {
      id: 'applied-guide',
      mascot:
        '오른쪽 「클래스 평가계획 등록」은 현재 선택 클래스에 적용된 평가계획이에요. 적용·삭제는 오른쪽 위 "저장하기"로 남겨 주세요. 살펴본 뒤 "다음"을 눌러 주세요.',
      targetSelector: '[data-tutorial-ep="applied-block"], .cp-evaluation-plan-applied-block',
      manualAdvance: true,
      blockTargetClick: true,
    },
    {
      id: 'save-all',
      mascot:
        '성취기준·평가계획은 각 등록/수정 모달에서 "등록"·"저장"을 누르면 바로 저장돼요.\n클래스에 적용하거나 목록에서 삭제한 뒤에는 오른쪽 위 "저장하기"로 적용 내용을 남겨 주세요. 변경이 없으면 비활성일 수 있어요.\n\n확인해 본 뒤 "다음"을 눌러 주세요.',
      targetSelector:
        '[data-tutorial-ep="save"], .cp-evaluation-plan-page--workspace .cp-btn-save-all',
      manualAdvance: true,
      section: '[평가계획] 저장',
      sectionMascot: '적용·삭제 등 남은 변경을 저장하는 구간이에요.',
    },
    {
      id: 'outro',
      mascot:
        '기본 평가계획 등록 튜토리얼을 마쳤어요! 적용한 평가는 연간진도·프로그램의 평가 기능에서 가져와 쓸 수 있어요.',
      actionLabel: '완료',
      presentation: 'celebration',
      cardTitle: '잘하셨어요!',
      placement: 'center',
    },
  ],
};

const evaluationPlan: TutorialDefinition = {
  ...evaluationPlanRaw,
  steps: evaluationPlanRaw.steps.map((step) => {
    const recover = RECOVER_BY_STEP_ID[step.id];
    if (!recover && !step.targetMissingHint) return step;
    return {
      ...step,
      ...(recover ? { recover } : {}),
      targetMissingHint: buildTutorialTargetMissingHint(recover, step.targetMissingHint),
    };
  }),
};

export default evaluationPlan;
